import React from 'react';
import { Button, FormGroup, ControlLabel, FormControl } from 'react-bootstrap';
import { observer } from 'mobx-react';
import { FieldGroup } from './FieldGroup';
import { Validator } from './Validator';
import { required, isEmail, isAlphanumeric } from '../utils/ValidatorRulesUtils';
import { AlertGenerator } from './AlertGenerator';
import { ChangeEvent } from 'react';
import ConfigurationStore from '../stores/ConfigurationStore';
import OrganizationStore from '../stores/OrganizationStore';
import { IOrganizationModel } from '../models/Organization';

type OrganizationProps = {
  buttonText : string,
  onSubmit : (org : any, parentId : number | null, onSuccess : () => void) => void,
  organizationStore : OrganizationStore,
  configurationStore : ConfigurationStore,
  initialState? : IOrganizationModel,
  closeModal? : () => void
};

type OrganizationState = {
  name: string,
  email: string,
  apiKey: string,
  parentId: number
}

@observer
class OrganizationFields extends React.Component<OrganizationProps, OrganizationState> {
  state: OrganizationState = {
    name: '',
    email: '',
    apiKey: '',
    parentId: 0
  }

  static rules = {
    name: [required],
    email: [isEmail],
    apiKey: [isAlphanumeric]
  };

  componentDidMount() {
    if(this.props.organizationStore !== undefined) {
      this.props.organizationStore.getOrganizations();
    }
  }

  componentDidUpdate(prevProps : any) {
    if (prevProps.initialState === this.props.initialState)
      return;

    const initialState = this.props.initialState || {};
    const parentId = (initialState as IOrganizationModel).parentId;
    if (parentId === void 0) {
      this.setState({ ...this.state, ...initialState });
    }
    
    this.setState({ ...this.state, ...initialState, parentId: parentId === null ? 0 : parentId});
  }

  updateField(name: any, event: ChangeEvent<EventTarget>) {
    const target = event.target as HTMLInputElement;
    let val : any = target.value;

    this.setState({
      [name]: val
    } as OrganizationState);
  }

  submit = (event : ChangeEvent<EventTarget>) => {
    event.preventDefault();

    const submitSuccess = () => {
      this.props.closeModal && this.props.closeModal();
    }
    const parentId = this.state.parentId === 0 ? null : this.state.parentId;
    this.props.onSubmit(this.state, parentId, submitSuccess);
  };

  render() {
    const { configurationStore, organizationStore, buttonText, initialState } = this.props;
    const { rules } = OrganizationFields;
    const { alertMessage } = configurationStore;
    const { organizations } = organizationStore;
    const hideAlert = configurationStore.hideAlert as () => {};
    let validParentOrganizaitons = organizations
      .filter(org => org.id !== this.props.organizationStore.currentOrganizationId)

    if (initialState !== void 0)
      validParentOrganizaitons = validParentOrganizaitons
        .filter(org => !(this.props.initialState as IOrganizationModel).hasChild(org.id));

    return (
      <form onSubmit={this.submit}>
        <AlertGenerator alertMessage={alertMessage} hideAlert={hideAlert}></AlertGenerator>
        <Validator rules={rules.name}
          fieldName='name' value={this.state.name} render={({ isValid, message }) => (
            <FieldGroup
              id='name'
              type='text'
              label='Name'
              onChange={this.updateField.bind(this, 'name')}
              value={this.state.name}
              isValid={isValid}
              message={message}
              required
            />
          )}
        />
        <Validator rules={rules.email}
          fieldName='email' value={this.state.email} render={({ isValid, message }) => (
            <FieldGroup
              id='email'
              type='text'
              label='Email address (optional)'
              onChange={this.updateField.bind(this, 'email')}
              value={this.state.email}
              isValid={isValid}
              message={message}
            />
          )}
        />
        <Validator rules={rules.apiKey}
          fieldName='apiKey' value={this.state.apiKey} render={({ isValid, message }) => (
            <FieldGroup
              id='apiKey'
              type='text'
              label='Api Key (optional)'
              onChange={this.updateField.bind(this, 'apiKey')}
              value={this.state.apiKey}
              isValid={isValid}
              message={message}
            />
          )}
        />

        <FormGroup controlId="parentSelect">
          <ControlLabel>Parent organization (optional)</ControlLabel>
          <FormControl componentClass="select"
            value={this.state.parentId}
            onChange={this.updateField.bind(this, 'parentId')}
          >
            <option value={0}>--Select--</option>
            {
              validParentOrganizaitons.map(org => {
                return <option value={org.id} key={org.id}>{org.name}</option>
              })
            }
          </FormControl>
        </FormGroup>
       
        <Button bsStyle='primary' type="submit">{buttonText}</Button>
      </form>
    );
  }
}

export { OrganizationFields };